import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import useImageUpload from "@/hooks/useImageUpload";
import { useDispatch, useSelector } from "react-redux";
import { addPart, updatePart } from "../store/useParts/usePartsThunk";
import {
  fetchCategoriesAndMakes,
  fetchModelsByMake,
  fetchPartNamesByCategoryId,
} from "../store/parts/partsAction";
import { getEffectivePlan } from "../utils/subscriptionUtils";

const validateField = (value, fieldName, validations) => {
  if (validations.required && !value && typeof value !== "number")
    return `${fieldName} is required.`;
  if (validations.minLength && value && value.length < validations.minLength)
    return `${fieldName} must be at least ${validations.minLength} characters.`;
  if (validations.maxLength && value && value.length > validations.maxLength)
    return `${fieldName} must be at most ${validations.maxLength} characters.`;
  if (validations.pattern && value && !validations.pattern.test(value))
    return `Invalid ${fieldName.toLowerCase()}. Example: ${
      validations.example || "format"
    }.`;
  if (
    validations.min !== undefined &&
    value &&
    parseFloat(value) < validations.min
  )
    return `${fieldName} must be at least ${validations.min}.`;
  if (
    validations.max !== undefined &&
    value &&
    parseFloat(value) > validations.max
  )
    return `${fieldName} must be no more than ${validations.max}.`;
  return "";
};

const isValidStripePaymentLink = (link) => {
  const trimmedLink = link.trim();

  if (trimmedLink === "") {
    return true;
  }

  if (!link || typeof link !== "string") {
    return false;
  }

  const stripePatterns = [
    /^https:\/\/buy\.stripe\.com\/[a-zA-Z0-9_-]+$/,
    /^https:\/\/checkout\.stripe\.com\/[a-zA-Z0-9_/-]+$/,
    /^https:\/\/buy\.stripe\.com\/test_[a-zA-Z0-9_-]+$/,
    /^https:\/\/checkout\.stripe\.com\/c\/pay\/[a-zA-Z0-9_-]+$/,
  ];

  return stripePatterns.some((pattern) => pattern.test(trimmedLink));
};

const generateYears = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = currentYear; year >= 1900; year--) {
    years.push(year);
  }
  return years;
};

export const usePartForm = (selectedPart) => {
  const { toast } = useToast();
  const dispatch = useDispatch();
  const { user, currentSubscription } = useSelector((state) => state.auth);
  const { loading } = useSelector((state) => state.parts);
  const { categories, makes, partModel } = useSelector((state) => state.parts);
  const { partNames } = useSelector((state) => state.parts);
  const navigate = useNavigate();
  const {
    images,
    handleImageChange,
    removeImage,
    resetImages,
    imageCount,
    uploadImages,
    setInitialImages,
  } = useImageUpload(5);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const currentPlan = getEffectivePlan(currentSubscription);

  const years = generateYears();

  const [formData, setFormData] = useState({
    year: "",
    make: "",
    makeId: "",
    makeOther: "",
    model: "",
    modelOther: "",
    partCategory: "",
    partCategoryId: "",
    partCategoryOther: "",
    partName: "",
    partNameOther: "",
    price: "",
    quantity: "1",
    postalCode: "",
    stripePaymentLink: "",
    detailedDescription: "",
  });

  const [errors, setErrors] = useState({});
  const [availableModels, setAvailableModels] = useState([]);
  const [availableParts, setAvailableParts] = useState([]);
  const [partAddedSuccessfully, setPartAddedSuccessfully] = useState(false);
  const [addedPartName, setAddedPartName] = useState("");
  const formRef = useRef(null);
  const { inventory } = useSelector((state) => state.useParts);
  const plan = getEffectivePlan(currentSubscription);

  const getMakeIdByName = (makeName) => {
    const make = makes?.find((m) => m.name === makeName);
    return make?.id || null;
  };

  const getCategoryIdByName = (categoryName) => {
    const category = categories?.find((cat) => cat.name === categoryName);
    return category?.id || null;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "stripePaymentLink") {
      if (value.trim() !== "" && !isValidStripePaymentLink(value)) {
        setErrors((prev) => ({
          ...prev,
          stripePaymentLink:
            "Please enter a valid Stripe payment link (e.g., https://buy.stripe.com/...)",
        }));
      } else {
        clearError(name);
      }
    } else {
      clearError(name);
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => {
      const newState = { ...prev, [name]: value };

      if (name === "make") {
        const makeId = getMakeIdByName(value);
        newState.makeId = makeId || "";

        if (value !== "Other" && makeId) {
          dispatch(fetchModelsByMake(makeId));
        }

        if (isInitialized && !isInitializing) {
          newState.makeOther = "";
          newState.model = "";
          newState.modelOther = "";
        }
        if (value !== "Other") {
          newState.makeOther = "";
        }
      }

      if (name === "model") {
        if (isInitialized && !isInitializing && value !== "Other") {
          newState.modelOther = "";
        }
      }

      if (name === "partCategory") {
        const categoryId = getCategoryIdByName(value);
        newState.partCategoryId = categoryId || "";

        const isOtherCategory =
          value?.toLowerCase() === "other" || value?.toLowerCase() === "others";

        if (!isOtherCategory && categoryId) {
          dispatch(fetchPartNamesByCategoryId(categoryId));
        }

        if (isInitialized && !isInitializing) {
          newState.partCategoryOther = "";
          newState.partName = "";
          newState.partNameOther = "";
        }
        if (!isOtherCategory) {
          newState.partCategoryOther = "";
        }
      }

      if (name === "partName" && value !== "Other") {
        if (isInitialized && !isInitializing) {
          newState.partNameOther = "";
        }
      }

      return newState;
    });
    clearError(name);
  };

  const clearError = (fieldName) => {
    setErrors((prev) => {
      if (prev[fieldName]) {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      }
      return prev;
    });
  };

  const validateForm = () => {
    const newErrors = {};
    newErrors.year = validateField(formData.year, "Year", { required: true });
    newErrors.make =
      formData.make === "Other"
        ? validateField(formData.makeOther, "Other Make", {
            required: true,
            minLength: 1,
          })
        : validateField(formData.make, "Make", { required: true });
    if (formData.make) {
      newErrors.model =
        formData.model === "Other"
          ? validateField(formData.modelOther, "Other Model", {
              required: true,
              minLength: 1,
            })
          : validateField(formData.model, "Model", { required: true });
    } else {
      newErrors.model = validateField(formData.model, "Model", {
        required: true,
      });
    }

    const isOtherCategory =
      formData.partCategory?.toLowerCase() === "other" ||
      formData.partCategory?.toLowerCase() === "others";

    newErrors.partCategory = isOtherCategory
      ? validateField(formData.partCategoryOther, "Other Category", {
          required: true,
          minLength: 2,
        })
      : validateField(formData.partCategory, "Part Category", {
          required: true,
        });

    newErrors.price = validateField(formData.price, "Price", {
      required: true,
      min: 0.01,
    });
    newErrors.quantity = validateField(formData.quantity, "Quantity", {
      required: true,
      min: 1,
      pattern: /^\d+$/,
      example: "e.g. 1, 10, 100",
    });
    newErrors.postalCode = validateField(formData.postalCode, "ZIP Code", {
      required: true,
      pattern: /^\d{5}$/,
      example: "12345",
    });

    if (formData.stripePaymentLink.trim() !== "") {
      if (!isValidStripePaymentLink(formData.stripePaymentLink)) {
        newErrors.stripePaymentLink =
          "Please enter a valid Stripe payment link (e.g., https://buy.stripe.com/...)";
      }
    }

    const finalErrors = Object.entries(newErrors).reduce(
      (acc, [key, value]) => {
        if (value) acc[key] = value;
        return acc;
      },
      {},
    );

    setErrors(finalErrors);
    return Object.keys(finalErrors).length === 0;
  };

  const resetFormForNewPart = () => {
    setIsInitializing(false);
    setFormData({
      year: "",
      make: "",
      makeId: "",
      makeOther: "",
      model: "",
      modelOther: "",
      partCategory: "",
      partCategoryId: "",
      partCategoryOther: "",
      partName: "",
      partNameOther: "",
      price: "",
      quantity: "1",
      postalCode: "",
      stripePaymentLink: "",
      detailedDescription: "",
    });
    resetImages();
    setErrors({});
    setAvailableModels([]);
    setAvailableParts([]);
    setPartAddedSuccessfully(false);
    setAddedPartName("");
    setIsInitialized(false);
    formRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Please check the form for errors.",
      });
      const firstErrorKey = Object.keys(errors).find((key) => errors[key]);
      if (firstErrorKey)
        formRef.current?.querySelector(`[name="${firstErrorKey}"]`)?.focus();
      return;
    }

    setIsSubmitting(true);
    if (!user) {
      toast({
        variant: "destructive",
        title: "Authentication Required",
        description: "Please log in to add parts.",
      });
      setIsSubmitting(false);
      return;
    }
    if (user.role !== "seller") {
      toast({
        variant: "destructive",
        title: "Permission Denied",
        description: "Only sellers can add parts.",
      });
      setIsSubmitting(false);
      return;
    }

    if (
      currentSubscription?.status === "active" &&
      ((plan === "free" && inventory?.length >= 10) ||
        (plan === "pro" && inventory?.length >= 500))
    ) {
      return;
    }

    try {
      const imageUrls = await uploadImages(user.uid);

      if (selectedPart) {
        await dispatch(
          updatePart({
            partId: selectedPart.id,
            formData,
            imageUrls,
          }),
        ).unwrap();

        navigate("/dashboard");
        toast({
          title: "Part Updated!",
          description: `${
            formData.partName || formData.partNameOther
          } has been updated successfully.`,
          variant: "success",
        });
      } else {
        await dispatch(
          addPart({ currentPlan, user, formData, imageUrls }),
        ).unwrap();
        navigate("/dashboard");
        toast({
          title: "Part Added!",
          description: `${
            formData.partName || formData.partNameOther
          } added to your inventory.`,
          variant: "success",
        });
      }

      setAddedPartName(formData.partName || formData.partNameOther);
      setPartAddedSuccessfully(true);
    } catch (error) {
      toast({
        variant: "destructive",
        title: selectedPart ? "Update Error" : "Save Error",
        description: `Could not ${
          selectedPart ? "update" : "save"
        } the part: ${error}`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (formData.make && formData.make !== "Other") {
      setAvailableModels(partModel || []);
    } else {
      setAvailableModels([]);
    }
  }, [partModel, formData.make]);

  useEffect(() => {
    if (formData.partCategory && formData.partCategory !== "Other") {
      setAvailableParts(partNames || []);
    } else {
      setAvailableParts([]);
    }
  }, [partNames, formData.partCategory]);

  // Initialize form with selectedPart data
  useEffect(() => {
    if (selectedPart) {
      setIsInitializing(true);

      const makeExists = makes?.some((m) => m.name === selectedPart.make);
      const categoryExists = categories?.some(
        (cat) => cat.name === selectedPart.category,
      );

      const makeId = getMakeIdByName(selectedPart.make);
      const categoryId =
        selectedPart.categoryId || getCategoryIdByName(selectedPart.category);

      // Determine if we should use "Other" for make
      const shouldUseMakeOther = !makeExists || selectedPart.sub_make;

      // Determine if we should use "Other" for model
      const shouldUseModelOther = selectedPart.sub_model;

      // Fetch models if make exists and we're not using "Other"
      if (makeId && makeExists && !shouldUseMakeOther) {
        dispatch(fetchModelsByMake(makeId));
      }

      // Check if category is "Other" from the list OR if sub_category exists
      const categoryIsOtherType =
        selectedPart.category?.toLowerCase() === "other" ||
        selectedPart.category?.toLowerCase() === "others";

      const shouldUseCategoryOther = selectedPart.sub_category;

      // Fetch part names if category exists and is NOT "Other" type and no sub_category
      if (
        categoryId &&
        categoryExists &&
        !categoryIsOtherType &&
        !shouldUseCategoryOther
      ) {
        dispatch(fetchPartNamesByCategoryId(categoryId));
      }

      // Determine if we should use "Other" for part name
      const shouldUsePartNameOther = selectedPart.sub_name;

      setFormData({
        year: selectedPart.year?.toString() || "",
        make: shouldUseMakeOther ? "Other" : selectedPart.make,
        makeId: makeId || "",
        makeOther: shouldUseMakeOther
          ? selectedPart.sub_make || selectedPart.make
          : "",
        model: shouldUseModelOther ? "Other" : selectedPart.model || "",
        modelOther: shouldUseModelOther ? selectedPart.sub_model : "",
        partCategory: categoryExists ? selectedPart.category : "other",
        partCategoryId: categoryId || "",
        partCategoryOther: shouldUseCategoryOther
          ? selectedPart.sub_category
          : "",
        partName: shouldUsePartNameOther ? "Other" : selectedPart.name || "",
        partNameOther: shouldUsePartNameOther ? selectedPart.sub_name : "",
        price: selectedPart.price?.toString() || "",
        quantity: selectedPart.quantity?.toString() || "1",
        postalCode: selectedPart.postalCode || "",
        stripePaymentLink: selectedPart.stripePaymentLink || "",
        detailedDescription: selectedPart.description || "",
      });

      if (selectedPart.imageUrls?.length > 0) {
        const initialImages = selectedPart.imageUrls.map((url) => ({
          url,
          preview: url,
          isExisting: true,
        }));
        setInitialImages(initialImages);
      } else {
        setInitialImages([]);
      }

      setIsInitialized(true);
      setIsInitializing(false);
    } else {
      resetFormForNewPart();
    }
  }, [selectedPart, categories, makes]);

  useEffect(() => {
    dispatch(fetchCategoriesAndMakes());
  }, [dispatch]);

  useEffect(() => {
    setIsInitialized(false);
    setIsInitializing(false);
  }, [selectedPart?.id]);

  return {
    formRef,
    formData,
    errors,
    isSubmitting: isSubmitting || loading,
    images,
    imageCount,
    availableModels,
    availableParts,
    partAddedSuccessfully,
    addedPartName,
    isInitialized,
    years,
    handleInputChange,
    handleSelectChange,
    handleImageChange,
    removeImage,
    handleSubmit,
    resetFormForNewPart,
    navigate,
  };
};
